/**
 * /zh/swot-analysis-template — SWOT 分析模板中文 SEO 页。与
 * SwotAnalysisTemplate.tsx 同构，原生中文，hreflang 双向。
 */
import { MarketingShell } from './MarketingShell';

export function SwotAnalysisTemplateZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/swot-analysis-template"
      documentTitle="SWOT 分析模板（不会过时的那种）· Draftlize"
      documentDescription="一份可直接抄的 SWOT 分析模板——优势、劣势、机会、威胁，附每个象限该问的问题——并讲清怎么让它不在对手一动时就过时。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">模板 · SWOT 分析</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            一份 SWOT 分析模板，<br/>
            <em>不会冻结在某一刻。</em>
          </h1>
          <p className="lede">
            你为一份 <em>SWOT 模板</em> 而来——四个象限、填满每个象限的提示。它在下面；抄进文档或幻灯片。但要知道它的翻车方式：SWOT 是对一片不肯静止的竞争格局的快照。这张表在你做出来的那个下午是准的，到对手上线、某个优势被商品化、或某个威胁成真的那一周就错了——而静态的 SWOT 根本不知道这些发生过。Draftlize 给你同样的四个象限，连着它们本应推动的定位决策。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">模板</span>
            <h2 className="serif">四个象限。<em>随处可抄。</em></h2>
          </div>
          <p>SWOT 从两个维度给因素分类：内部 vs 外部、有利 vs 有害。这就是整个框架。下面的提示是每个象限里到底该问什么——这是一份有用的 SWOT 和四串套话之间的区别。</p>
        </div>

        <div style={{ border: '1px solid var(--ink-0)', background: 'var(--paper-0)', fontFamily: "'Inter', sans-serif" }}>
          {[
            { k: '优势 Strengths', hint: '内部 · 有利', body: '你比替代方案做得更好、且你能掌控的东西——渠道优势、专有数据、团队能力、品牌。问：我们今天靠什么赢，这个优势是持久的、还是快被竞争抹平的？含糊的优势（「团队很棒」）没用；点名那个具体的边。' },
            { k: '劣势 Weaknesses', hint: '内部 · 有害', body: '你落后或暴露、且你能掌控的地方——产品的缺口、单薄的团队、技术债、没有企业级故事。问：我们在哪些单子上输，竞争对手会先攻哪里？这里的诚实就是全部价值；一份自我恭维的 SWOT 是浪费一个下午。' },
            { k: '机会 Opportunities', hint: '外部 · 有利', body: '你能利用、但不由你掌控的世界变化——一个新平台、一种行为转变、对手失手、一项法规。问：刚发生了什么我们能骑上去的变化？机会按定义是外部的；「我们可以做功能 X」是计划，不是机会。' },
            { k: '威胁 Threats', hint: '外部 · 有害', body: '可能伤到你、但不由你掌控的力量——一个重金入局者、一个改条款的平台、需求的转向。问：什么会让我们的优势变得无关紧要？威胁是 SWOT 真正值钱的地方，也正是对手一动时最快过时的那个象限。' },
          ].map((f, i) => (
            <div key={f.k} style={{ display: 'grid', gridTemplateColumns: '190px 1fr', gap: 24, padding: '20px 22px', borderTop: i === 0 ? 0 : '1px dashed var(--rule)', alignItems: 'baseline' }}>
              <div>
                <span className="serif" style={{ fontSize: 18, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.3 }}>{f.k}</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--amber)', fontWeight: 700 }}>{f.hint}</span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.75, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: '.04em', color: 'var(--ink-3)', textAlign: 'center', marginTop: 18 }}>
          重点不是这张表——而是它隐含的动作：用优势抓机会，用补劣势挡威胁。另见 <a href="/zh/competitive-analysis-template" style={{ color: 'var(--amber)' }}>竞品分析模板</a>。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">SWOT 为什么<em>会过时。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">它是对移动格局的一张快照</h3>
            <p>SWOT 捕的是某一刻的竞争格局。但威胁和机会是外部的、一直在动——对手上线、平台转向、需求掉头。表不会跟着动，于是几周内它真有一部分悄悄变假，而你说不出是哪一部分。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">它和本应驱动的决策脱钩</h3>
            <p>SWOT 的意义是为定位和策略提供输入。但分析住在一份文档里、<a href="/zh/product-strategy" style={{ color: 'var(--amber)' }}>策略</a> 住在另一份里，于是当威胁变了，没有任何东西往前走到那个「假设它不存在」的策略。SWOT 变成工作坊的产物，而不是「活」的输入。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">没人在触发点重跑它</h3>
            <p>SWOT 是按日历重做的——一年一次、规划时——而不是在那个让它失效的事情发生时。于是你据以行动的那一版，是上次 offsite 的版本，描述着一片此后已悄然走远的竞争格局。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一张表，<em>接上线。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每个因素成为一张卡片</h3>
            <p>在 Draftlize 里，一个优势、劣势、机会或威胁是一张卡片——一个带着背后证据的论断——而不是表里的一个项目符号。一个竞品威胁连到确立它的那条观察，于是「这还成立吗」有了一个你能核对的答案。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">因素一变，依赖它的标 stale</h3>
            <p>对手上线了那个曾是你优势的东西。你更新那张卡片，所有倚靠它的定位决策和策略赌注自动标记 stale——就像构建系统让被改文件的下游全部失效一样。SWOT 驱动策略，而不是给 deck 做装饰。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 agent 读到当前格局</h3>
            <p>当 AI agent 经 MCP 帮你做定位或下一步竞争动作，它读到的是「活」的 SWOT 因素——而不是去年的表——于是它的建议把上个月上线的那个对手算进去，而不是上次规划时存在的那个。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          SWOT 在你做出来的那个下午是真的。它所描述的竞争格局，不会一直保持那个姿势。
        </blockquote>
        <cite>留着四个象限，把每个因素连到它本应推动的决策。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'SWOT 分析的四个部分是什么？', a: '优势（Strengths）、劣势（Weaknesses）、机会（Opportunities）、威胁（Threats）。优势和劣势是内部的、在你掌控之内；机会和威胁是外部的、在你掌控之外。这张表按「内部 vs 外部」和「有利 vs 有害」给因素分类。' },
            { q: 'SWOT 和竞品分析有什么区别？', a: 'SWOT 是对你自身处境、按四象限做的高层快照；竞品分析是对具体对手、在具体维度上更深入、结构化的对比。SWOT 是快速定向，竞品分析是喂给它的详细功课。' },
            { q: '怎么真正用 SWOT（而不只是填满它）？', a: '价值在于它隐含的动作：用优势去抓机会，用补劣势去挡威胁。一份产出四串清单、却没有任何决策的 SWOT 是浪费时间——每个因素都该改变一个定位或策略选择。' },
            { q: 'SWOT 该多久更新一次？', a: '在某个因素变化时，而不是按固定日历。威胁和机会是外部的、一直在动，所以只在年度规划时重做的 SWOT，通常描述的是一片已经移动了的竞争格局。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">让你的 SWOT 保持最新<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            填上面的四个象限，或在 Draftlize 里把它们建成连着定位决策的卡片——这样对手一动，受影响的优势、威胁、以及压在其上的策略全都标 stale。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
